import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { MetricsStrip, isLoadedSnapshot, useDialPacing } from '../pacing';
import { IconChevronRight, IconX } from './icons';

export type TopBarProps = {
	onNextLead: () => void;
	onClose: () => void;
	/**
	 * Optional "Skip lead" affordance. When provided, the TopBar renders a
	 * subdued button between the pacing strip and "Next lead" so reps can skip
	 * a queue entry without dialing first (legacy queue's `tempSkipReasons` /
	 * `skipReasons` modal entry-point). Hidden when undefined so existing
	 * tests that don't supply it don't have to render the button.
	 */
	onSkipLead?: () => void;
	/** Disabled while no lead is loaded or while a skip mutation is in-flight. */
	skipLeadDisabled?: boolean;
	/** Optional nav affordances rendered on the left of the action row. */
	nav?: React.ReactNode;
};

/**
 * Daily metrics row rendered beneath the action row. Surfaces the full legacy
 * StatusBanner tile set (prospecting dials, dials, connections, follow-ups,
 * demos-as-tigers) plus the points/dials pacing bar, so the top bar and the
 * milestone modal show the same numbers. Skeleton/error states collapse to a
 * thin placeholder so pacing never blocks the view.
 */
const TopBarMetrics: FC = () => {
	const { snapshot } = useDialPacing();
	if (!isLoadedSnapshot(snapshot)) {
		return <span className={css(styles.metricsSkeleton)} />;
	}
	const { score, goals, projectedScore, isSalesCoffee } = snapshot;
	return <MetricsStrip variant='top' input={{ score, goals, projectedScore, isSalesCoffee }} />;
};

export const TopBar: FC<TopBarProps> = ({ onNextLead, onClose, onSkipLead, skipLeadDisabled, nav }) => {
	return (
		<div className={css(styles.bar)}>
			<div className={css(styles.actionRow)}>
				<div className={css(styles.navSlot)}>{nav}</div>
				<div className={css(styles.actions)}>
					{onSkipLead && (
						<>
							<button
								type='button'
								onClick={onSkipLead}
								disabled={skipLeadDisabled}
								title='Skip this lead with a reason'
								className={css(styles.skipBtn, skipLeadDisabled && styles.skipBtnDisabled)}
							>
								Skip lead
							</button>
							<div className={css(styles.divider)} />
						</>
					)}
					<button type='button' onClick={onNextLead} title='Advance to the next lead' className={css(styles.nextBtn)}>
						Next lead
						<IconChevronRight size={13} sw={2.2} />
					</button>
					<div className={css(styles.divider)} />
					<button
						type='button'
						onClick={onClose}
						title='Exit focused view'
						aria-label='Exit focused view'
						className={css(styles.closeBtn)}
					>
						<IconX size={14} />
					</button>
				</div>
			</div>
			<div className={css(styles.metricsRow)}>
				<TopBarMetrics />
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	// Two-row header at the top of the CENTER column. Row 1 (the 52px action row)
	// keeps the LEAD label + globe + kebab (rendered inside LeftColumn) on the
	// same Y-line as the actions; row 2 carries the daily metrics strip.
	bar: {
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		borderBottom: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	actionRow: {
		height: 52,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		padding: '0 16px',
	},
	navSlot: {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
	},
	metricsRow: {
		display: 'flex',
		alignItems: 'center',
		minHeight: 48,
		padding: '6px 16px',
		borderTop: '1px solid var(--border)',
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		borderRadius: 999,
		padding: 4,
		boxShadow: 'var(--shadow-sm)',
	},
	nextBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 28,
		padding: '0 12px 0 10px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'var(--ink)',
		color: 'var(--bg)',
		border: 'none',
		cursor: 'pointer',
		fontFamily: 'inherit',
	},
	// Skip-lead is a secondary action — same height as Next lead but a quieter
	// outline treatment so the primary forward path stays visually dominant.
	skipBtn: {
		display: 'inline-flex',
		alignItems: 'center',
		height: 28,
		padding: '0 12px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'transparent',
		color: 'var(--ink)',
		border: '1px solid var(--border)',
		cursor: 'pointer',
		fontFamily: 'inherit',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
		},
	},
	skipBtnDisabled: {
		opacity: 0.5,
		cursor: 'not-allowed',
		':hover': {
			background: 'transparent',
		},
	},
	divider: {
		width: 1,
		height: 18,
		background: 'var(--border)',
		margin: '0 2px',
	},
	closeBtn: {
		width: 28,
		height: 28,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 999,
		background: 'transparent',
		color: 'var(--ink-muted)',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	metricsSkeleton: {
		display: 'inline-block',
		width: 220,
		height: 12,
		borderRadius: 6,
		background: 'var(--border)',
		opacity: 0.55,
	},
});
